import { Dialog, Transition } from "@headlessui/react";
import { Address } from "@helium/crypto";
import { XIcon } from "@heroicons/react/solid";
import { Base64 } from "js-base64";
import { chunk, find, get, isEmpty, pick, reduce, sumBy } from "lodash";
import moment from "moment";
import Tooltip from "rc-tooltip";
import React, { Fragment, useCallback, useMemo, useRef } from "react";
import { useCommissionReports } from ".";
import ContactComponent from "../../components/ContactComponent";
import ExternalLink from "../../components/ExternalLink";
import InfoIcon from "../../components/icons/InfoIcon";
import { renderDate } from "../../pages/Map/HotspotDetails/HotspotInfoTab";
import { getCommissionReportPeriodText } from "../../pages/Workspace/CommissionReportsData/CommissionReports";
import { enhancedPaymentMethods } from "../../pages/Workspace/ContactsData/PaymentMethodFormSidebar";
import classNames from "../../utils/classNames";
import { downloadCsv, downloadFile } from "../../utils/csv";
import { formatDateToLocale } from "../../utils/dates";
import generateHeliumWalletDeepLink, {
  HeliumWalletPayment
} from "../../utils/generateHeliumWalletDeepLink";
import {
  CommissionPaymentStatus,
  CommissionReport,
  Contact,
  ContactCommissions,
  ContactPayment,
  PaymentMethod,
  PaymentTransaction,
  PaymentTypeKey
} from "../../utils/types";
import useCurrency, { CurrencyKey } from "../../utils/useCurrency";
import { BONES_PER_HNT, useHntMarket } from "../../utils/useHntMarket";
import { useContacts } from "../contacts";
import { ContactPaymentContextKey } from "../contacts/ContactsProvider";
import { PAYMENT_MEMO_MAX_CHARACTERS } from "./HntPaymentSidebar";
import MarkAsPaidButton from "./MarkAsPaidButton";

interface Props {}

const CommissionPaymentSummarySidebar: React.FC<Props> = () => {
  const dialogRef = useRef(null);
  const { displayInCurrency } = useHntMarket();
  const { getCurrencyTitle, currencyDisplay, getCurrencyOption } =
    useCurrency();
  const { contacts, getDefaultPaymentMethod } = useContacts();
  const {
    contactCommissionsSummarySidebarConfig,
    commissionPaymentSummarySidebarConfig,
    setCommissionPaymentSummarySidebarConfig,
    setContactCommissionsSummarySidebarConfig,
    hntPaymentSidebarConfig,
    setHntPaymentSidebarConfig
  } = useCommissionReports();

  const isParentSidebar = useMemo(
    () =>
      [
        contactCommissionsSummarySidebarConfig.parentSidebar,
        hntPaymentSidebarConfig.parentSidebar
      ].includes("commission-payment-summary"),
    [
      contactCommissionsSummarySidebarConfig.parentSidebar,
      hntPaymentSidebarConfig.parentSidebar
    ]
  );

  const closeSidebar = useCallback(() => {
    if (isParentSidebar) return;

    setCommissionPaymentSummarySidebarConfig({ show: false });
  }, [isParentSidebar, setCommissionPaymentSummarySidebarConfig]);

  const {
    show,
    commissionReport,
    validContactCommissions,
    invalidContactCommissions,
    paidContactCommissions
  } = useMemo(() => {
    const {
      show,
      commissionReport,
      contactCommissionsToPay,
      getPaymentStatus
    } = commissionPaymentSummarySidebarConfig;

    const {
      validContactCommissions,
      invalidContactCommissions,
      paidContactCommissions
    } = reduce(
      contactCommissionsToPay,
      (
        acc: {
          validContactCommissions: ValidContactCommissions[];
          invalidContactCommissions: InvalidContactCommissions[];
          paidContactCommissions: PaidContactCommissions[];
        },
        contactCommissions
      ) => {
        if (!commissionReport || !getPaymentStatus) return acc;

        const { contact, totalCommissionValue } = contactCommissions;

        const { status, paymentTransaction, contactPayment } =
          getPaymentStatus(contactCommissions);

        if (status === "paid") {
          acc.paidContactCommissions.push({
            ...contactCommissions,
            contactPayment,
            paymentTransaction
          });

          return acc;
        }

        const fullContact = contacts[contact.uuid];

        if (!fullContact) {
          acc.invalidContactCommissions.push({
            ...contactCommissions,
            reason: "Missing contact in workspace"
          });

          return acc;
        }

        if (totalCommissionValue === 0) {
          acc.invalidContactCommissions.push({
            ...contactCommissions,
            reason: "No commissions to be paid"
          });

          return acc;
        }

        const paymentMethod = getDefaultPaymentMethod(
          contact.uuid,
          commissionReport.currency
        );

        if (!paymentMethod) {
          acc.invalidContactCommissions.push({
            ...contactCommissions,
            reason: `No default payment method for ${getCurrencyTitle(
              commissionReport.currency
            )}`
          });

          return acc;
        }

        const parsedMetadata = JSON.parse(paymentMethod.metadata);
        const walletAddress = get(parsedMetadata, "walletAddress");

        if (
          commissionReport.currency === CurrencyKey.hnt &&
          !Address.isValid(walletAddress)
        ) {
          acc.invalidContactCommissions.push({
            ...contactCommissions,
            reason: "Invalid HNT wallet address"
          });

          return acc;
        }

        const contactWithSameWalletAddress =
          walletAddress &&
          find(acc.validContactCommissions, {
            walletAddress
          });
        if (contactWithSameWalletAddress) {
          acc.invalidContactCommissions.push({
            ...contactCommissions,
            reason: `Duplicate HNT wallet address (${contactWithSameWalletAddress.contact.name})`
          });

          return acc;
        }

        acc.validContactCommissions.push({
          ...contactCommissions,
          walletAddress,
          paymentMethod,
          fullContact
        });

        return acc;
      },
      {
        validContactCommissions: [],
        invalidContactCommissions: [],
        paidContactCommissions: []
      }
    );

    return {
      show,
      commissionReport,
      validContactCommissions,
      invalidContactCommissions,
      paidContactCommissions
    };
  }, [
    commissionPaymentSummarySidebarConfig,
    contacts,
    getDefaultPaymentMethod,
    getCurrencyTitle
  ]);

  const generateDeepLink = useCallback(
    (contactCommissionsToPay: ValidContactCommissions[], memo: string) => {
      const payments: HeliumWalletPayment[] = [];

      contactCommissionsToPay.forEach((item) => {
        payments.push({
          payee: item.walletAddress,
          amount: item.totalCommissionValue,
          memo: Base64.encode(memo || "")
        });
      });

      generateHeliumWalletDeepLink(payments);
    },
    []
  );

  const renderAmount = useCallback(
    (amount: number) => {
      const currency = commissionReport?.currency || CurrencyKey.hnt;
      return currency === CurrencyKey.hnt
        ? displayInCurrency(amount, {
            withHntSuffix: true,
            needsDivision: true
          })
        : currencyDisplay(currency, amount);
    },
    [commissionReport?.currency, currencyDisplay, displayInCurrency]
  );

  const renderHntPaymentSections = useCallback(
    (commissionReport: CommissionReport) => (
      <>
        <div className="md:hidden mb-6">
          <h3 className="text-sm font-medium text-gray-400 mb-4">
            Helium Wallet
            <Tooltip
              overlay={
                <>
                  This will load the payment transaction in the Helium Wallet
                  App. Please ensure you have the{" "}
                  <ExternalLink
                    className="inline-flex"
                    text="iOS app"
                    url="https://apps.apple.com/br/app/helium-hnt-wallet/id1609525848?l=en"
                  />{" "}
                  or{" "}
                  <ExternalLink
                    className="inline-flex"
                    text="android app"
                    url="https://play.google.com/store/apps/details?id=com.helium.wallet.app"
                  />{" "}
                  installed before using this payment method.
                </>
              }
            >
              <InfoIcon className="ml-1 inline-flex h-4 w-4" />
            </Tooltip>
          </h3>
          <div className="bg-gray-50 divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800 sm:rounded-lg">
            <div className="px-3 py-3 md:py-4 md:px-6 whitespace-nowraprelative flex md:items-center md:space-x-3 group flex-col md:flex-row">
              <div className="flex flex-1 items-center mb-2 md:mb-0 md:items-start md:flex-col focus:outline-none">
                <div className="text-sm md:text-base font-medium text-gray-900 dark:text-white truncate">
                  {validContactCommissions.length} contact
                  {validContactCommissions.length === 1 ? "" : "s"}
                </div>
                <div className="text-sm font-medium text-gray-900 dark:text-white truncate ml-1 md:m-0">
                  {renderAmount(
                    sumBy(validContactCommissions, "totalCommissionValue")
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <button
                  disabled={isEmpty(validContactCommissions)}
                  className="md:ml-4 inline-flex justify-center py-2 px-1.5 sm:px-4 border border-transparent shadow-sm text-xs md:text-sm font-medium rounded-md text-white bg-hotspotty-600 hover:bg-hotspotty-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-500 items-center disabled:opacity-50 disabled:cursor-default disabled:hover:bg-hotspotty-600"
                  onClick={() =>
                    generateDeepLink(
                      validContactCommissions,
                      commissionReport?.uuid.substr(
                        -PAYMENT_MEMO_MAX_CHARACTERS
                      )
                    )
                  }
                >
                  Pay with Helium Wallet
                </button>
                <MarkAsPaidButton
                  onSuccess={closeSidebar}
                  commissionReportUuid={commissionReport.uuid}
                  contactPaymentValues={validContactCommissions.map(
                    (values) => ({
                      contact_uuid: values.contact.uuid,
                      currency: CurrencyKey.hnt,
                      amount: values.totalCommissionValue,
                      context: ContactPaymentContextKey.wallet
                    })
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="hidden md:block">
          <h3 className="text-sm font-medium text-gray-400 mb-4">
            QR code payments
          </h3>
          <div className="bg-gray-50 divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800 sm:rounded-lg">
            {chunk(validContactCommissions, MAX_BATCH_PAYMENT_SIZE).map(
              (contactCommissionsToPay, index) => (
                <div
                  key={index}
                  className="px-3 py-3 md:py-4 md:px-6 whitespace-nowraprelative flex md:items-center md:space-x-3 group flex-col md:flex-row"
                >
                  <div className="flex flex-1 items-center mb-2 md:mb-0 md:items-start md:flex-col focus:outline-none">
                    <div className="text-sm md:text-base font-medium text-gray-900 dark:text-white truncate">
                      {contactCommissionsToPay.length} contact
                      {contactCommissionsToPay.length === 1 ? "" : "s"}
                    </div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate ml-1 md:m-0">
                      {renderAmount(
                        sumBy(contactCommissionsToPay, "totalCommissionValue")
                      )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <button
                      disabled={isEmpty(contactCommissionsToPay)}
                      className="md:ml-4 inline-flex justify-center py-2 px-1.5 sm:px-4 border border-transparent shadow-sm text-xs md:text-sm font-medium rounded-md text-white bg-hotspotty-600 hover:bg-hotspotty-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-500 items-center disabled:opacity-50 disabled:cursor-default disabled:hover:bg-hotspotty-600"
                      onClick={() => {
                        setHntPaymentSidebarConfig({
                          show: true,
                          commissionReport,
                          contactCommissionsToPay,
                          parentSidebar: "commission-payment-summary"
                        });
                      }}
                    >
                      Pay{" "}
                      {validContactCommissions.length > MAX_BATCH_PAYMENT_SIZE
                        ? `batch ${index + 1}`
                        : "with QR code"}
                    </button>
                    <MarkAsPaidButton
                      onSuccess={closeSidebar}
                      commissionReportUuid={commissionReport.uuid}
                      contactPaymentValues={contactCommissionsToPay.map(
                        (values) => ({
                          contact_uuid: values.contact.uuid,
                          currency: CurrencyKey.hnt,
                          amount: values.totalCommissionValue,
                          context: ContactPaymentContextKey.qr
                        })
                      )}
                    />
                  </div>
                </div>
              )
            )}
          </div>

          <h3 className="text-sm font-medium text-gray-400 mb-4 mt-6">
            CLI payment
          </h3>
          <div className="bg-gray-50 divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800  sm:rounded-lg">
            <div className="px-3 py-3 md:py-4 md:px-6 whitespace-nowraprelative flex md:items-center md:space-x-3 group flex-col md:flex-row">
              <div className="flex flex-1 items-center mb-2 md:mb-0 md:items-start md:flex-col focus:outline-none">
                <div className="text-sm md:text-md font-medium text-gray-900 dark:text-white truncate">
                  {validContactCommissions.length} contact
                  {validContactCommissions.length === 1 ? "" : "s"}
                </div>
                <div className="text-sm font-medium text-gray-900 dark:text-white truncate ml-1 md:m-0">
                  {renderAmount(
                    sumBy(validContactCommissions, "totalCommissionValue")
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <button
                  disabled={isEmpty(validContactCommissions)}
                  className="md:ml-4 inline-flex justify-center py-2 px-1.5 sm:px-4 border border-transparent shadow-sm text-xs md:text-sm font-medium rounded-md text-white bg-hotspotty-600 hover:bg-hotspotty-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-500 items-center disabled:opacity-50 disabled:cursor-default disabled:hover:bg-hotspotty-600"
                  onClick={() => {
                    const memo = commissionReport.uuid.substr(
                      -PAYMENT_MEMO_MAX_CHARACTERS
                    );
                    downloadFile(
                      getHntCliMulltiPaymentJson(validContactCommissions, memo),
                      `commission-report-${
                        commissionReport.identifier
                      }-cli-multi-payment-${moment()
                        .utc()
                        .format("YYYY_MM_DD_HH_mm")}.json`,
                      "application/json"
                    );
                  }}
                >
                  Download JSON file
                </button>
                <MarkAsPaidButton
                  onSuccess={closeSidebar}
                  commissionReportUuid={commissionReport.uuid}
                  contactPaymentValues={validContactCommissions.map(
                    (values) => ({
                      contact_uuid: values.contact.uuid,
                      currency: CurrencyKey.hnt,
                      amount: values.totalCommissionValue,
                      context: ContactPaymentContextKey.cli
                    })
                  )}
                />
              </div>
            </div>
          </div>

          <h3 className="text-sm font-medium text-gray-400 mb-4 md:mt-6">
            CSV file
          </h3>
          <div className="bg-gray-50 divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800  sm:rounded-lg">
            <div className="px-3 py-3 md:py-4 md:px-6 whitespace-nowraprelative flex md:items-center md:space-x-3 group flex-col md:flex-row">
              <div className="flex flex-1 items-center mb-2 md:mb-0 md:items-start md:flex-col focus:outline-none">
                <div className="text-sm md:text-base font-medium text-gray-900 dark:text-white truncate">
                  {validContactCommissions.length} contact
                  {validContactCommissions.length === 1 ? "" : "s"}
                </div>
                <div className="text-sm font-medium text-gray-900 dark:text-white truncate ml-1 md:m-0">
                  {renderAmount(
                    sumBy(validContactCommissions, "totalCommissionValue")
                  )}
                </div>
              </div>
              <div className="flex items-center">
                <button
                  disabled={isEmpty(validContactCommissions)}
                  className="md:ml-4 inline-flex justify-center py-2 px-1.5 sm:px-4 border  border-transparent shadow-sm text-xs md:text-sm font-medium rounded-md text-white bg-hotspotty-600 hover:bg-hotspotty-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-500 items-center disabled:opacity-50 disabled:cursor-default disabled:hover:bg-hotspotty-600"
                  onClick={() => {
                    const contactPaymentsToExport = validContactCommissions.map(
                      ({
                        contact,
                        totalCommissionValue,
                        paymentMethod,
                        fullContact
                      }) => {
                        const { title, fields } =
                          enhancedPaymentMethods[paymentMethod.type];

                        return {
                          name: contact.name,
                          email: fullContact.email || "",
                          phone: fullContact.phone || "",
                          amount: totalCommissionValue / BONES_PER_HNT,
                          currency: commissionReport.currency,
                          payment_method: title,
                          ...reduce(
                            pick(
                              JSON.parse(paymentMethod.metadata) || {},
                              fields
                            ),
                            (acc: { [key: string]: string }, value, key) => {
                              acc[`${paymentMethod.type}_${key}`] = value;
                              return acc;
                            },
                            {}
                          )
                        };
                      }
                    );

                    downloadCsv(
                      contactPaymentsToExport,
                      `commission-report-${commissionReport.identifier}-${
                        commissionReport.currency
                      }-payments-${moment().utc().format("YYYY_MM_DD_HH_mm")}`
                    );
                  }}
                >
                  Download CSV file
                </button>
                <MarkAsPaidButton
                  onSuccess={closeSidebar}
                  commissionReportUuid={commissionReport.uuid}
                  contactPaymentValues={validContactCommissions.map(
                    (values) => ({
                      contact_uuid: values.contact.uuid,
                      currency: commissionReport.currency,
                      amount: values.totalCommissionValue,
                      context: ContactPaymentContextKey.csv
                    })
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    ),
    [
      closeSidebar,
      generateDeepLink,
      renderAmount,
      setHntPaymentSidebarConfig,
      validContactCommissions
    ]
  );

  const renderFiatPaymentSections = useCallback(
    (commissionReport: CommissionReport) => {
      const currencyOption = getCurrencyOption(commissionReport.currency);
      const wiseCurrency = currencyOption.wiseCurrency;
      const paypalCurrency = currencyOption.paypalCurrency;

      const contactCommissionsWithWisePaymentMethod =
        validContactCommissions.filter(
          ({ paymentMethod }) =>
            paymentMethod && paymentMethod.type === PaymentTypeKey.wiseByEmail
        );

      const contactCommissionsWithPaypalPaymentMethod =
        validContactCommissions.filter(
          ({ paymentMethod }) =>
            paymentMethod && paymentMethod.type === PaymentTypeKey.paypal
        );

      return (
        <>
          <h3 className="text-sm font-medium text-gray-400 mb-4">CSV file</h3>
          <div className="bg-gray-50 divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800 sm:rounded-lg">
            <div className="px-6 py-4 whitespace-nowraprelative flex items-center space-x-3 group">
              <div className="flex-1 flex-col min-w-0 focus:outline-none">
                <div className="text-sm md:text-base font-medium text-gray-900 dark:text-white truncate">
                  {validContactCommissions.length} contact
                  {validContactCommissions.length === 1 ? "" : "s"}
                </div>
                <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {renderAmount(
                    sumBy(validContactCommissions, "totalCommissionValue")
                  )}
                </div>
              </div>
              <button
                disabled={isEmpty(validContactCommissions)}
                className="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-hotspotty-600 hover:bg-hotspotty-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-500 items-center disabled:opacity-50 disabled:cursor-default disabled:hover:bg-hotspotty-600"
                onClick={() => {
                  const contactPaymentsToExport = validContactCommissions.map(
                    ({
                      contact,
                      totalCommissionValue,
                      paymentMethod,
                      fullContact
                    }) => {
                      const { title, fields } =
                        enhancedPaymentMethods[paymentMethod.type];

                      return {
                        name: contact.name,
                        email: fullContact.email || "",
                        phone: fullContact.phone || "",
                        amount: totalCommissionValue,
                        currency: commissionReport.currency,
                        payment_method: title,
                        ...reduce(
                          pick(
                            JSON.parse(paymentMethod.metadata) || {},
                            fields
                          ),
                          (acc: { [key: string]: string }, value, key) => {
                            acc[`${paymentMethod.type}_${key}`] = value;
                            return acc;
                          },
                          {}
                        )
                      };
                    }
                  );

                  downloadCsv(
                    contactPaymentsToExport,
                    `commission-report-${commissionReport.identifier}-${
                      commissionReport.currency
                    }-payments-${moment().utc().format("YYYY_MM_DD_HH_mm")}`
                  );
                }}
              >
                Download CSV file
              </button>
              <MarkAsPaidButton
                onSuccess={closeSidebar}
                commissionReportUuid={commissionReport.uuid}
                contactPaymentValues={validContactCommissions.map((values) => ({
                  contact_uuid: values.contact.uuid,
                  currency: commissionReport.currency,
                  amount: values.totalCommissionValue,
                  context: ContactPaymentContextKey.csv
                }))}
              />
            </div>
          </div>
          {!!wiseCurrency && (
            <>
              <h3 className="flex items-center text-sm font-medium text-gray-400 mb-4 mt-6">
                Wise by email
                <Tooltip
                  overlay={
                    <>
                      Download a CSV file compatible with Wise.com batch
                      payments that sends out an email to your contacts to
                      collect their payment.{" "}
                      <ExternalLink
                        className="inline-flex"
                        text="Click here"
                        url="https://wise.com/us/blog/everything-about-batch-payments"
                      />{" "}
                      to read more. The CSV file will only be generated for
                      contacts with their default payment method for{" "}
                      {getCurrencyTitle(commissionReport.currency)} set to "Wise
                      by email".
                    </>
                  }
                >
                  <InfoIcon className="inline-flex h-4 w-4 ml-1" />
                </Tooltip>
              </h3>
              <div className="bg-gray-50 divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800 sm:rounded-lg">
                <div className="px-6 py-4 whitespace-nowraprelative flex items-center space-x-3 group">
                  <div className="flex-1 flex-col min-w-0 focus:outline-none">
                    <div className="text-sm md:text-base font-medium text-gray-900 dark:text-white truncate">
                      {contactCommissionsWithWisePaymentMethod.length} contact
                      {contactCommissionsWithWisePaymentMethod.length === 1
                        ? ""
                        : "s"}
                    </div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {renderAmount(
                        sumBy(
                          contactCommissionsWithWisePaymentMethod,
                          "totalCommissionValue"
                        )
                      )}
                    </div>
                  </div>
                  <button
                    disabled={isEmpty(contactCommissionsWithWisePaymentMethod)}
                    className="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-hotspotty-600 hover:bg-hotspotty-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-500 items-center disabled:opacity-50 disabled:cursor-default disabled:hover:bg-hotspotty-600"
                    onClick={() => {
                      const paymentReference = `Hotspot commission for ${getCommissionReportPeriodText(
                        commissionReport
                      )}`;

                      const contactPaymentsToExport =
                        contactCommissionsWithWisePaymentMethod.map(
                          ({
                            contact,
                            totalCommissionValue,
                            paymentMethod
                          }) => {
                            const { email } =
                              JSON.parse(paymentMethod.metadata) || {};

                            return {
                              name: contact.name,
                              recipientEmail: email,
                              paymentReference,
                              amountCurrency: "source",
                              amount: totalCommissionValue,
                              sourceCurrency: wiseCurrency,
                              targetCurrency: wiseCurrency,
                              type: "EMAIL"
                            };
                          }
                        );

                      downloadCsv(
                        contactPaymentsToExport,
                        `commission-report-${commissionReport.identifier}-${
                          commissionReport.currency
                        }-wise-by-email-payments-${moment()
                          .utc()
                          .format("YYYY_MM_DD_HH_mm")}`
                      );
                    }}
                  >
                    Download CSV file
                  </button>
                  <MarkAsPaidButton
                    onSuccess={closeSidebar}
                    commissionReportUuid={commissionReport.uuid}
                    contactPaymentValues={contactCommissionsWithWisePaymentMethod.map(
                      (values) => ({
                        contact_uuid: values.contact.uuid,
                        currency: commissionReport.currency,
                        amount: values.totalCommissionValue,
                        context: ContactPaymentContextKey.wiseByEmail
                      })
                    )}
                  />
                </div>
              </div>
            </>
          )}
          {!!paypalCurrency && (
            <>
              <h3 className="flex items-center text-sm font-medium text-gray-400 mb-4 mt-6">
                Paypal - Payouts Web
                <Tooltip
                  overlay={
                    <>
                      Download a CSV file compatible with Payouts Web by PayPal.{" "}
                      <ExternalLink
                        className="inline-flex"
                        text="Click here"
                        url="https://developer.paypal.com/docs/regional/th/payouts/integrate/payouts-web/#link-uploadapaymentfile"
                      />{" "}
                      to read more. The CSV file will only be generated for
                      contacts with their default payment method for{" "}
                      {getCurrencyTitle(commissionReport.currency)} set to
                      "PayPal".
                    </>
                  }
                >
                  <InfoIcon className="inline-flex h-4 w-4 ml-1" />
                </Tooltip>
              </h3>
              <div className="bg-gray-50 divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800 sm:rounded-lg">
                <div className="px-6 py-4 whitespace-nowraprelative flex items-center space-x-3 group">
                  <div className="flex-1 flex-col min-w-0 focus:outline-none">
                    <div className="text-sm md:text-base font-medium text-gray-900 dark:text-white truncate">
                      {contactCommissionsWithPaypalPaymentMethod.length} contact
                      {contactCommissionsWithPaypalPaymentMethod.length === 1
                        ? ""
                        : "s"}
                    </div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {renderAmount(
                        sumBy(
                          contactCommissionsWithPaypalPaymentMethod,
                          "totalCommissionValue"
                        )
                      )}
                    </div>
                  </div>
                  <button
                    disabled={isEmpty(
                      contactCommissionsWithPaypalPaymentMethod
                    )}
                    className="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-hotspotty-600 hover:bg-hotspotty-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-500 items-center disabled:opacity-50 disabled:cursor-default disabled:hover:bg-hotspotty-600"
                    onClick={() => {
                      const paymentReference = `Hotspot commission for ${getCommissionReportPeriodText(
                        commissionReport
                      )}`;

                      const contactPaymentsToExport =
                        contactCommissionsWithPaypalPaymentMethod.map(
                          ({
                            contact,
                            totalCommissionValue,
                            paymentMethod
                          }) => {
                            const { email } =
                              JSON.parse(paymentMethod.metadata) || {};

                            return {
                              email,
                              totalCommissionValue,
                              paypalCurrency,
                              customerId: contact.uuid,
                              paymentReference,
                              type: "PAYPAL"
                            };
                          }
                        );

                      downloadCsv(
                        contactPaymentsToExport,
                        `commission-report-${commissionReport.identifier}-${
                          commissionReport.currency
                        }-paypal-payments-${moment()
                          .utc()
                          .format("YYYY_MM_DD_HH_mm")}`,
                        { delimiter: ",", header: false }
                      );
                    }}
                  >
                    Download CSV file
                  </button>
                  <MarkAsPaidButton
                    onSuccess={closeSidebar}
                    commissionReportUuid={commissionReport.uuid}
                    contactPaymentValues={contactCommissionsWithPaypalPaymentMethod.map(
                      (values) => ({
                        contact_uuid: values.contact.uuid,
                        currency: commissionReport.currency,
                        amount: values.totalCommissionValue,
                        context: ContactPaymentContextKey.paypal
                      })
                    )}
                  />
                </div>
              </div>
            </>
          )}
        </>
      );
    },
    [
      closeSidebar,
      getCurrencyOption,
      getCurrencyTitle,
      renderAmount,
      validContactCommissions
    ]
  );

  const renderContent = useMemo(() => {
    if (!commissionReport) return null;

    return (
      <div className="flex-1 py-6 divide-y divide-gray-200 dark:divide-gray-500 overflow-y-auto">
        <div className="px-4 sm:px-6 space-y-6">
          <div className="border-b border-gray-200 dark:border-gray-500 mb-5 pb-2 md:pb-5">
            <h3 className="text-sm md:text-lg md:leading-6 font-medium text-gray-900 dark:text-white flex flex-col md:flex-row md:items-center justify-between">
              <span>
                Valid pending contact payments ({validContactCommissions.length}
                )
              </span>
              <span>
                {renderAmount(
                  sumBy(validContactCommissions, "totalCommissionValue")
                )}
              </span>
            </h3>
          </div>

          <div>
            {isEmpty(validContactCommissions) ? (
              <div className="mt-1 px-4 md:px-6 py-4 bg-white divide-y divide-gray-200 text-xs md:text-sm font-medium text-gray-500 shadow overflow-hidden sm:rounded-lg dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800">
                No valid contacts with pending payments are available.
              </div>
            ) : (
              <div className="mt-1 flex flex-col w-full overflow-hidden">
                {commissionReport.currency === CurrencyKey.hnt
                  ? renderHntPaymentSections(commissionReport)
                  : renderFiatPaymentSections(commissionReport)}
              </div>
            )}
          </div>

          {!isEmpty(paidContactCommissions) && (
            <>
              <div className="border-b border-gray-200 dark:border-gray-500 pb-2 md:pb-5">
                <h3 className="text-sm md:text-lg md:leading-6 font-medium text-gray-900 dark:text-white flex flex-col md:flex-row md:items-center justify-between">
                  <span>Paid contacts ({paidContactCommissions.length})</span>
                  <span>
                    {renderAmount(
                      sumBy(paidContactCommissions, "totalCommissionValue")
                    )}
                  </span>
                </h3>
              </div>

              {paidContactCommissions.map((contactCommissions) => (
                <div
                  key={contactCommissions.contact.uuid}
                  className="flex items-start justify-between text-sm font-medium group cursor-pointer"
                  onClick={() => {
                    setContactCommissionsSummarySidebarConfig({
                      show: true,
                      commissionReport,
                      contactCommissions,
                      parentSidebar: "commission-payment-summary",
                      getPaymentStatus:
                        commissionPaymentSummarySidebarConfig.getPaymentStatus
                    });
                  }}
                >
                  <div className="relative flex items-center space-x-3">
                    <ContactComponent
                      contactId={contactCommissions.contact.uuid}
                      fallbackName={contactCommissions.contact.name}
                      subtitle={
                        <p className="text-xs md:text-sm font-normal text-green-500 truncate">
                          {contactCommissions.contactPayment &&
                            `Marked as paid on ${formatDateToLocale(
                              contactCommissions.contactPayment.created_at
                            )}`}
                          {contactCommissions.paymentTransaction &&
                            !contactCommissions.contactPayment &&
                            `Paid on ${renderDate(
                              contactCommissions.paymentTransaction.time
                            )} by QR payment, confirmed on-chain`}
                        </p>
                      }
                    />
                  </div>
                  <div className="text-xs md:text-base font-semibold text-gray-900 dark:text-white">
                    {renderAmount(contactCommissions.totalCommissionValue)}
                  </div>
                </div>
              ))}
            </>
          )}

          {!isEmpty(invalidContactCommissions) && (
            <>
              <div className="border-b border-gray-200 dark:border-gray-500 pb-2 md:pb-5">
                <h3 className="text-sm md:text-lg md:leading-6 font-medium text-gray-900 dark:text-white flex flex-col md:flex-row md:items-center justify-between">
                  <span>
                    Invalid pending contact payments (
                    {invalidContactCommissions.length})
                  </span>
                  <span>
                    {renderAmount(
                      sumBy(invalidContactCommissions, "totalCommissionValue")
                    )}
                  </span>
                </h3>
              </div>

              {invalidContactCommissions.map((contactCommissions) => (
                <div
                  key={contactCommissions.contact.uuid}
                  className="flex items-start justify-between text-sm font-medium group cursor-pointer"
                  onClick={() => {
                    setContactCommissionsSummarySidebarConfig({
                      show: true,
                      commissionReport,
                      contactCommissions,
                      parentSidebar: "commission-payment-summary",
                      getPaymentStatus:
                        commissionPaymentSummarySidebarConfig.getPaymentStatus
                    });
                  }}
                >
                  <div className="relative flex items-center space-x-3">
                    <ContactComponent
                      contactId={contactCommissions.contact.uuid}
                      fallbackName={contactCommissions.contact.name}
                      subtitle={
                        <p className="text-xs md:text-sm font-normal text-red-500 truncate">
                          {contactCommissions.reason}
                        </p>
                      }
                    />
                  </div>
                  <div className="text-xs md:text-base font-semibold text-gray-900 dark:text-white">
                    {renderAmount(contactCommissions.totalCommissionValue)}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    );
  }, [
    commissionPaymentSummarySidebarConfig.getPaymentStatus,
    commissionReport,
    invalidContactCommissions,
    paidContactCommissions,
    renderAmount,
    renderFiatPaymentSections,
    renderHntPaymentSections,
    setContactCommissionsSummarySidebarConfig,
    validContactCommissions
  ]);

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-20 overflow-hidden"
        open={isParentSidebar}
        onClose={() => {}}
        initialFocus={dialogRef}
      >
        <div ref={dialogRef} className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity">
            <div className="flex w-full h-full" onClick={closeSidebar}></div>
          </Dialog.Overlay>

          <div
            className={classNames(
              "fixed inset-y-0 pl-16 max-w-full right-0 flex duration-500",
              isParentSidebar && "pr-40"
            )}
          >
            <div className="absolute inset-0" />
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="relative w-screen max-w-xl dark:text-white">
                <div className="max-w-3xl xl:max-w-6xl h-full">
                  <div className="h-full flex flex-col bg-white shadow-xl dark:bg-gray-900">
                    <div className="flex-1 h-0 flex flex-col">
                      <div className="flex-none py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                        <div className="flex items-center justify-between">
                          <Dialog.Title className="text-lg font-medium text-white">
                            Payment summary
                          </Dialog.Title>
                          <div className="ml-3 h-7 flex items-center">
                            <button
                              type="button"
                              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                              onClick={closeSidebar}
                            >
                              <span className="sr-only">Close panel</span>
                              <XIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {renderContent}
                    </div>
                    <div className="flex-shrink-0 px-4 py-4 flex justify-end">
                      <button
                        type="button"
                        className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
                        onClick={closeSidebar}
                      >
                        Cancel
                      </button>
                      <button
                        className="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 items-center disabled:opacity-50 disabled:cursor-default disabled:hover:bg-hotspotty-400"
                        onClick={closeSidebar}
                      >
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CommissionPaymentSummarySidebar;

//
// Utils
//

// TODO: extend this for other currencies
export interface CommissionPaymentSummarySidebarConfig {
  show: boolean;
  commissionReport?: CommissionReport;
  contactCommissionsToPay?: ContactCommissions[];
  getPaymentStatus?: (
    contactCommissions: ContactCommissions
  ) => CommissionPaymentStatus;
}

export const MAX_BATCH_PAYMENT_SIZE = 25;

interface InvalidContactCommissions extends ContactCommissions {
  reason: string;
}

interface PaidContactCommissions extends ContactCommissions {
  contactPayment: ContactPayment | undefined;
  paymentTransaction: PaymentTransaction | undefined;
}

export interface ValidContactCommissions extends ContactCommissions {
  walletAddress: string;
  paymentMethod: PaymentMethod;
  fullContact: Contact;
}

const getHntCliMulltiPaymentJson = (
  validContactCommissions: ValidContactCommissions[],
  memo: string
) =>
  JSON.stringify(
    validContactCommissions.map(({ totalCommissionValue, walletAddress }) => ({
      address: walletAddress,
      amount: totalCommissionValue / BONES_PER_HNT
      // memo: Base64.encode(memo),
    })),
    null,
    2
  );
